import {
  convertToDDMM,
  convertToDegrees,
  convertToDMS,
} from './formatPosition';
import { CursorPositionFormat } from './enum';

export interface FormattedCoordinate {
  lat: string;
  lon: string;
}

/**
 * Validates that coordinates are within valid EPSG:4326 bounds
 * @param longitude - Longitude in decimal degrees (-180 to 180)
 * @param latitude - Latitude in decimal degrees (-90 to 90)
 * @returns true if coordinates are valid
 */
export function isValidCoordinate(
  longitude: number,
  latitude: number,
): boolean {
  return (
    Number.isFinite(longitude) &&
    Number.isFinite(latitude) &&
    longitude >= -180 &&
    longitude <= 180 &&
    latitude >= -90 &&
    latitude <= 90
  );
}

/**
 * Formats coordinates for display based on user's preferred format setting.
 * Used by cursor coordinate display and copy-to-clipboard functionality.
 *
 * @param longitude - Longitude in decimal degrees (EPSG:4326)
 * @param latitude - Latitude in decimal degrees (EPSG:4326)
 * @param format - The display format preference (DDMM, DMS, or Degrees)
 * @returns Formatted lat/lon strings
 */
export function formatCoordinate(
  longitude: number,
  latitude: number,
  format?: CursorPositionFormat,
): FormattedCoordinate {
  let lat: string;
  let lon: string;

  switch (format) {
    case CursorPositionFormat.positionDegrees:
      lat = convertToDegrees(latitude, 'lat');
      lon = convertToDegrees(longitude, 'lon');
      break;
    case CursorPositionFormat.positionDMS:
      lat = convertToDMS(latitude, 'lat');
      lon = convertToDMS(longitude, 'lon');
      break;
    default:
      // Default to DDMM format
      lat = convertToDDMM(latitude, 'lat');
      lon = convertToDDMM(longitude, 'lon');
  }

  return { lat, lon };
}

/**
 * Formats coordinates as a single string for copying to clipboard.
 * Format: "{lat} {lon}" (space-separated, lat first to match cursor display order)
 *
 * @param longitude - Longitude in decimal degrees (EPSG:4326)
 * @param latitude - Latitude in decimal degrees (EPSG:4326)
 * @param format - The display format preference (DDMM, DMS, or Degrees)
 * @returns Formatted coordinate string for clipboard, or null if invalid
 */
export function formatCoordinateForCopy(
  longitude: number,
  latitude: number,
  format?: CursorPositionFormat,
): string | null {
  if (!isValidCoordinate(longitude, latitude)) {
    return null;
  }

  const { lat, lon } = formatCoordinate(longitude, latitude, format);
  return `${lat} ${lon}`;
}
